import { Accordion, Select, TextInput } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconFilter, IconVariable } from '@tabler/icons-react';
import type { EmailExtractionType } from '~/common/stably-email/stably-email-types';
import type { EmailFilters, ValueType } from '~/server/logic/steps/operation/types';

export type ReceiveEmailFormValues = {
  toEmailAddressVariable: string;
  toEmailAddressType: ValueType;
  extractionType: EmailExtractionType | undefined;
  variableName: string;
  customExtractionPrompt: string;
  filters: EmailFilters;
};

/**
 * The "Filters" and "Extraction" accordion inside the config popover. Split out
 * of ReceiveEmailPart to keep that file under the max-lines limit.
 */
export function ReceiveEmailFields({
  form,
  readonly,
  numberOfActiveFilters,
  defaultOpenAccordionItems,
}: {
  form: UseFormReturnType<ReceiveEmailFormValues>;
  readonly?: boolean;
  numberOfActiveFilters: number;
  defaultOpenAccordionItems: string[];
}) {
  return (
    <Accordion
      variant="separated"
      multiple
      defaultValue={defaultOpenAccordionItems}
      classNames={{ control: 'px-2 text-blue-600 dark:text-blue-400 text-sm', content: 'px-2' }}
    >
      <Accordion.Item value="filters">
        <Accordion.Control icon={<IconFilter size={14} />}>
          Filters {numberOfActiveFilters > 0 ? `(${numberOfActiveFilters})` : ''}
        </Accordion.Control>
        <Accordion.Panel>
          <div className="flex flex-col gap-4">
            <TextInput
              label="Sender"
              disabled={readonly}
              placeholder="sender@example.com"
              {...form.getInputProps('filters.sender')}
            />
            <TextInput
              label="Subject"
              description="Matches emails where the subject contains this text. Quote the subject to match exactly."
              disabled={readonly}
              placeholder="Subject"
              {...form.getInputProps('filters.subject')}
            />
          </div>
        </Accordion.Panel>
      </Accordion.Item>
      <Accordion.Item value="extraction">
        <Accordion.Control icon={<IconVariable size={16} />}>
          {/* TODO: add inidcator */}
          Extraction
        </Accordion.Control>
        <Accordion.Panel>
          <div className="flex flex-col gap-4">
            <span>Extract content from the body of the email</span>
            <Select
              disabled={readonly}
              // Fixes the outside click issue
              comboboxProps={{ withinPortal: false }}
              label="Extraction Type"
              clearable
              // TODO: do this nicer
              data={[
                { value: 'otp' satisfies EmailExtractionType, label: 'OTP (One-Time Password)' },
                { value: 'magic-link' satisfies EmailExtractionType, label: 'Magic Link' },
                { value: 'custom-extraction' satisfies EmailExtractionType, label: 'Custom Extraction' },
              ]}
              {...form.getInputProps('extractionType')}
            />
            {!!form.values.extractionType && (
              <>
                <TextInput
                  label="Stored Variable Name"
                  disabled={readonly}
                  placeholder="myExtractedVar"
                  {...form.getInputProps('variableName')}
                />
                {form.values.extractionType === 'custom-extraction' && (
                  <TextInput
                    label="Extraction Prompt"
                    placeholder="e.g. 'The account recovery instructions'"
                    disabled={readonly}
                    {...form.getInputProps('customExtractionPrompt')}
                  />
                )}
              </>
            )}
          </div>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
